import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import type { ClientPractitionerNote } from '@/lib/clients/activity-timeline';
import { formatDateLocale, safeToDate } from '@/lib/utils';
import { Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ClientNotesTabProps {
  notes: ClientPractitionerNote[];
  loading: boolean;
  error?: string | null;
  currentUserUid?: string;
  onCreate: (text: string) => Promise<void>;
  onDelete: (noteId: string) => Promise<void>;
}

function authorLabel(note: ClientPractitionerNote): string {
  return note.createdByName || note.createdByEmail || 'Practitioner';
}

export function ClientNotesTab({
  notes,
  loading,
  error,
  currentUserUid,
  onCreate,
  onDelete,
}: ClientNotesTabProps) {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function handleSave() {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await onCreate(draft);
      setDraft('');
    } catch {
      // Parent surfaces the error toast; keep the draft so the user can retry.
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await onDelete(deleteId);
      setDeleteId(null);
    } catch {
      // Parent surfaces the error toast; keep the dialog open for retry.
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <Card className="border-none shadow-md" data-testid="client-notes-tab">
        <CardHeader>
          <CardTitle>Internal notes</CardTitle>
          <CardDescription>
            Practitioner-only notes — timestamped with author for audit trail
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <p
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              role="alert"
              data-testid="client-notes-error"
            >
              {error}
            </p>
          )}
          <div className="space-y-2">
            <Textarea
              data-testid="client-note-input"
              placeholder="Add a session note, follow-up reminder, or clinical observation…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={4}
            />
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving || !draft.trim()}
                data-testid="client-note-save"
              >
                {saving ? 'Saving…' : 'Save note'}
              </Button>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-muted-foreground">Loading notes…</p>
          ) : notes.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No notes yet.</p>
          ) : (
            <ul className="space-y-4">
              {notes.map((note) => {
                const created = safeToDate(note.createdAt);
                const canDelete = currentUserUid && note.createdByUid === currentUserUid;
                return (
                  <li
                    key={note.id}
                    className="rounded-lg border bg-muted/20 p-4 space-y-2"
                    data-testid="client-note-item"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline">{authorLabel(note)}</Badge>
                        {created && (
                          <time dateTime={created.toISOString()}>
                            {formatDateLocale(created, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </time>
                        )}
                      </div>
                      {canDelete && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => setDeleteId(note.id)}
                          aria-label="Delete note"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-sm whitespace-pre-wrap break-words">{note.text}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && !deleting && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. The note will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmDelete();
              }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
